@if ((openWithParams$ | async) !== null) {
  <div class="datepicker-container" [class.datepicker-container--embedded]="embedded">
    @if (datepickerLabels$ | async; as uiLabels) {
      @if (renderDatepicker$ | async) {
        <fcom-datepicker
          [uiLabels]="uiLabels"
          [dateLabels]="dateLabels$ | async"
          [topNotification]="
            (hasDiscountCode$ | async) === true
              ? ('bookingWidget.priceCalendar.discountCodeNotification' | finLocalization)
              : undefined
          "
          [topNotificationIcon]="SvgLibraryIcon.DISCOUNT_BLOCK"
          [titleAreaConfiguration]="{
            titleAreaStyle: 'ps-large-x ps-xsmall-y',
            title: datePickerTitleLabel$ | async | finLocalization,
            titleStyle: 'font-heading-4 font-title-medium',
          }"
          [isDateRange]="(tripType$ | async) === TripType.RETURN"
          [displayMonths]="displayMonths"
          [embedded]="embedded"
          [showCloseButton]="!embedded"
          [selectableDatesRange]="calendarRange"
          [selectedDates]="datepickerSelectedDates$ | async"
          [doneBtnHidden]="true"
          [showTags]="false"
          [dayTemplate]="dayTemplate"
          [headerTemplate]="
            (type === PriceCalendarType.PRICES && (isLoading$ | async)) ||
            (calendarPrices$ | async)?.histogram?.length > 0
              ? calendarHeader
              : undefined
          "
          [scrollToMonthIndex]="scrollToHistogramMonth$ | async"
          [showAddReturn]="(tripType$ | async) === TripType.ONEWAY && showAddReturn"
          [subTitle]="(subTitle$ | async) ? subTitleTemplate : undefined"
          [disabledDateRanges]="disabledDateRanges$ | async"
          (selectedDatesChange)="updateCalendarDates($event)"
          (close)="enableNewSearchAutomatically ? onClose() : closeModal()"
          (monthChange)="setHistogramMonth($event?.monthArrayIndex)"
          (addReturn)="addReturn()"
        />
      }
    }
  </div>

  <ng-template #subTitleTemplate>
    @if (subTitle$ | async; as subTitle) {
      <h3 class="font-body-1 subTitle medium-type font-label-medium" [style]="CMS_STYLE_OVERRIDE">
        <span>
          {{ subTitle.origin }}
        </span>
        <span class="flex--nogrow ps-xxsmall-x">
          <fcom-icon
            class="icon-small"
            [name]="
              (tripType$ | async) === TripType.RETURN ? SvgLibraryIcon.RETURN_TRIP : SvgLibraryIcon.AIRCRAFT_HORIZONTAL
            "
          />
        </span>
        <span>
          {{ subTitle.destination }}
        </span>
      </h3>
    }
  </ng-template>
  <div
    class="df-tablet-up flex flex--middle ps-large-x grey-50-bg surface-disabled-bg font-body-1 ps-small price-calendar-footer font-body-medium"
    [class.price-calendar-sticky-footer]="
      !embedded && ((datesSelected$ | async) || (pricesAvailable$ | async) === false)
    "
    data-testid="price-calendar-footer"
  >
    <span class="flex flex--align-start flex--space-between fill price-calendar-footer-container">
      <ng-container>
        @if (showTripPriceFrom && type === PriceCalendarType.PRICES) {
          @if ((noPricesAvailableForDates$ | async) || (pricesAvailable$ | async) === false) {
            <span data-testid="date-modal-price">{{
              'bookingWidget.priceCalendar.searchForPrices.' + CmsTripTypeMap[tripType$ | async] | finLocalization
            }}</span>
          } @else {
            <span data-testid="date-modal-price">
              <span
                >{{
                  ((tripType$ | async) === TripType.ONEWAY
                    ? 'bookingWidget.priceCalendar.oneWayPerAdult'
                    : 'bookingWidget.priceCalendar.roundTripPerAdult'
                  ) | finLocalization
                }}
              </span>
              <span class="bold-type">{{
                pricePerAdult$ | async | finPrice: { stripZeroCents: true, useSymbol: true } | finEmpty: '&ndash;'
              }}</span>
            </span>
          }
        }

        @if (showFlexibleDatesSelection) {
          <span class="flex--end">
            <fcom-checkbox-input
              [parentForm]="flexibleDatesForm"
              [controlName]="'flexibleDates'"
              data-testid="flexible-dates"
            >
              <span>{{ 'priceCalendar.flexibleDates' | finLocalization }}</span>
            </fcom-checkbox-input>
          </span>
        }
      </ng-container>
    </span>
    <div class="done-button align-center" [class.flex]="enableNewSearchAutomatically">
      @if (enableNewSearchAutomatically) {
        <fcom-button
          data-testid="travel-dates-cancel"
          class="ms-small-r"
          [theme]="ButtonTheme.LIGHT_SECONDARY_OUTLINE"
          [text]="'actions.cancel' | finLocalization"
          [aria]="{ label: 'actions.cancel' | finLocalization, role: 'button' }"
          (clickBtn)="onClose()"
        />

        <fcom-button
          data-testid="travel-dates-search"
          [theme]="ButtonTheme.LIGHT_PRIMARY_NORDIC_BLUE"
          [text]="'bookingSearch.search' | finLocalization"
          [aria]="{ label: 'bookingSearch.search' | finLocalization, role: 'button' }"
          [disabled]="!isSearchEnabled"
          (clickBtn)="startNewSearch()"
        />
      } @else {
        <fcom-button
          [theme]="ctaButtonTheme()"
          [text]="ctaLabel"
          [disabled]="ctaDisabled$ | async"
          (clickBtn)="emitCTAClicked()"
          data-testid="price-calendar-cta-button"
        />
      }
    </div>
  </div>
}

<ng-template #calendarHeader>
  <fin-histogram
    [barData]="(calendarPrices$ | finMap: 'histogram' | async) ?? []"
    [selected]="selectedHistogramMonth$ | async"
    [isActive]="(openWithParams$ | async) !== undefined"
    [isLoading]="(isLoading$ | async) ?? false"
    (barSelected)="handleHistogramClick($event)"
    (barsScrolled)="handleBarsScrolled($event)"
  />
</ng-template>

<ng-template #dayTemplate let-dayValue="dayValue" let-dayString="dayString">
  <div
    class="calendar-day"
    *finLet="(calendarPrices$ | async)?.calendar?.[dayValue.id] as price"
    [attr.data-testid]="'calendar-day-' + dayValue.id"
    fcomPopover
    [enablePopover]="
      (enablePriceSearchPopover$ | async) && dayValue.id >= (firstDateToShowPriceSearchPopover$ | async)?.id
    "
    [options]="popoverOptions"
    [content]="popoverContent"
  >
    <div
      class="calendar-day-number font-heading-5 font-title-small"
      [class.heather-700-text]="price?.isLowestPrice"
      [class.text-icon-accent]="price?.isLowestPrice"
    >
      {{ dayString }}
    </div>
    @if ((this.isLoading$ | async) === true) {
      <div class="flex flex--center ps-xxsmall">
        <div class="grey-bar"></div>
      </div>
    } @else if ((calendarPrices$ | async)?.calendar) {
      <div
        class="calendar-day-price font-body-2 regular-type font-body-small"
        [ngClass]="{
          'heather-700-text text-icon-accent bold-type': price?.isLowestPrice,
        }"
        [attr.data-testid]="'calendar-price-' + dayValue.id"
      >
        <ng-container
          *ngTemplateOutlet="
            priceTemplate;
            context: {
              amount:
                dayValue.id >= (travelDates$ | async)?.departureDate?.id &&
                dayValue.id < (travelDates$ | async)?.returnDate?.id &&
                (tripType$ | async) === TripType.RETURN
                  ? undefined
                  : price?.amount,
              isPriceAvailable: (calendarPrices$ | async)?.histogram,
            }
          "
        />
      </div>
    } @else {
      <div class="calendar-day-price font-body-2 regular-type font-body-small">
        <ng-container *ngTemplateOutlet="priceTemplate; context: { amount: undefined, isPriceAvailable: false }" />
      </div>
    }
    <ng-template #priceTemplate let-amount="amount" let-isPriceAvailable="isPriceAvailable">
      @if (isPriceAvailable) {
        {{ amount | finPrice: { abbreviate: true, useCustomFormat: true } | finEmpty: '&nbsp;' }}
      }
    </ng-template>

    @if (price) {
      <div class="fill absolute ps-xxsmall-x" [attr.data-testid]="'calendar-price-border-' + dayValue.id">
        @if (price?.isLowestPrice) {
          <div
            class="day-border rounded-medium"
            [class.heather-700-bg]="price?.isLowestPrice"
            [class.surface-accent]="price?.isLowestPrice"
          ></div>
        }
      </div>
    }
  </div>
</ng-template>

<ng-template #popoverContent>
  <span class="font-body-2 font-body-small">{{
    'bookingWidget.priceCalendar.searchForPricesTooltip' | finLocalization
  }}</span>
</ng-template>
